import React, { useContext, useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { OEContext } from '@aytm/app/stats/page/comments'

import { CSS_SUPPORTS_STICKY } from '@aytm/lib/common/func'
import { questionOldIdToId, removeLogicFromText } from '@aytm/app/stats/page/comments/lib/utils'
import {
  DEFAULT_SELECTED_QUESTION,
  ORDER_BY_OPTIONS_IMAGES,
  PER_PAGE_OPTIONS_IMAGES
} from '@aytm/app/stats/page/comments/lib/constants'

import downloadAndPackImages from '@aytm/app/stats/page/comments/lib/download/invoke'

//components
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { Paginator } from 'primereact/paginator'

import style from '@aytm/app/stats/page/comments/styles/header.module.scss'

const SurveyHeaderComments = ({
  readOnly,
  questionsList,
  currentResponse,
  onSelectedQuestionChange,
  onPageChange,
  fetchData
}) => {
  const { searchState, dispatch } = useContext(OEContext)

  const { request, totalPages, totalImageResponses, isLoading, isProcessing } = searchState
  const { selectedQuestion, pageSize, orderBy, orderDirection, currentPage } = request

  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState(0)

  const headerTop = useMemo(() => {
    const stickyHeader = document.querySelector('.stats-StickyHeader')

    if (stickyHeader) {
      const stickyHeaderRect = stickyHeader.getBoundingClientRect()
      return stickyHeaderRect.height + 20
    } else return 20

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchState])

  const isDownloadDisabled = useMemo(
    () => !totalImageResponses || isDownloading || isLoading || isProcessing,
    [totalImageResponses, isDownloading, isLoading, isProcessing]
  )

  const question = useMemo(
    () => questionsList.find(q => q.value == selectedQuestion) || null,
    [questionsList, selectedQuestion]
  )
  const questionsListOptions = useMemo(
    () =>
      questionsList
        .filter(item => !item.isOptionHidden)
        .map(item => Object.assign(item, { text: removeLogicFromText(item.text.replace(/<br>/g, ' ')) })),
    [questionsList]
  )

  const orderByOptions = useMemo(
    () =>
      ORDER_BY_OPTIONS_IMAGES.filter(item =>
        'similarity' !== item.value ? true : 'on' == window.features.oe_sort_similarity?.status
      ).map(item => {
        const newItem = Object.assign({}, item)

        if (newItem.forType && (!question || (question && question.type != newItem.forType))) {
          newItem.disabled = true
        }

        if (orderBy == newItem.value) {
          newItem.text = `${newItem.text} ${'asc' == orderDirection ? '&uarr;' : '&darr;'}`
        }

        return newItem
      }),
    [orderDirection, orderBy, question]
  )

  const onSelectOrderBy = event => {
    const req = {}
    if (event.value == orderBy) {
      req.orderDirection = 'asc' == orderDirection ? 'desc' : 'asc'
    } else {
      req.orderBy = event.value
      req.orderDirection = ORDER_BY_OPTIONS_IMAGES.find(option => option.value == event.value).defaultOrder
    }

    dispatch({ type: 'SET_PARAM', request: { name: 'isPreserveState', value: true } })
    dispatch({
      type: 'SET_REQUEST_PARAM_SET',
      request: req
    })
  }

  const onSelectQuestion = event => {
    const selected = event.value
    dispatch({ type: 'SET_PARAM', request: { name: 'isPreserveState', value: true } })
    dispatch({
      type: 'SET_REQUEST_PARAM_SET',
      request: {
        selectedQuestion: selected,
        currentPage: 1
      }
    })
    if (onSelectedQuestionChange && 'function' == typeof onSelectedQuestionChange) {
      onSelectedQuestionChange(selected)
    }
  }

  const onLoadPerPage = useCallback(
    event => {
      const newPageSize = event.value
      if (pageSize !== newPageSize) {
        const newTotalPages = Math.ceil(totalImageResponses / newPageSize)
        const newCurrentPage = Math.ceil(
          null !== currentResponse ? (pageSize * (currentPage - 1) + currentResponse.index + 1) / newPageSize : 1
        )

        dispatch({
          type: 'SET_PARAMS',
          request: { value: { totalPages: newTotalPages, isProcessing: true, isPreserveState: true } }
        })
        dispatch({
          type: 'SET_REQUEST_PARAM_SET',
          request: {
            pageSize: newPageSize,
            currentPage: newCurrentPage
          }
        })
      }
    },
    [dispatch, pageSize, currentPage, totalImageResponses, currentResponse]
  )

  const onEveryFileCallback = (percent = 0) => {
    setDownloadProgress(Math.ceil(percent))
  }

  const onFinishCallback = () => {
    setIsDownloading(false)
    setDownloadProgress(0)
  }

  const onDownloadAllClick = () => {
    setIsDownloading(true)
    const filename = `AYTM-Survey-${window.surveyId}-Image-Response-${
      selectedQuestion !== DEFAULT_SELECTED_QUESTION ? selectedQuestion : 'ALL'
    }.zip`
    fetchData({
      customParams: {
        pageSize: totalImageResponses,
        currentPage: 1
      },
      customFetchDataCallback: ({ cs }) => {
        const files = cs.reduce((acc, response) => {
          Object.keys(response.answer).forEach(k => {
            if (response.answer[k].i && response.answer[k].i.original) {
              acc.push({
                filename: `AYTM-Survey-${window.surveyId}-Image-Response-${response.response.uid}_${questionOldIdToId(
                  k
                )}.jpg`,
                url: response.answer[k].i.original.url
              })
            }
          })
          return acc
        }, [])
        downloadAndPackImages(filename, files, onEveryFileCallback, onFinishCallback)
      }
    })
  }

  const itemTemplate = option =>
    option.hidden ? null : (
      <span className={`${option.icon ? 'p-item-label ' : ''}p-lvl-${option.lvl || 0}`}>
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
      </span>
    )

  const valueTemplate = (option, _props) =>
    option ? (
      <>
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.label || option.text }}></span>
      </>
    ) : (
      <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }}></span>
    )

  const orderByItemTemplate = option =>
    option.hidden ? null : (
      <span onClick={option.value == orderBy ? () => onSelectOrderBy(option) : null} data-key={option.key}>
        <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
      </span>
    )

  const pageSizeValueTemplate = option =>
    totalImageResponses < option.value ? (
      <>{totalImageResponses} found</>
    ) : (
      <>
        {option.value}/{totalImageResponses}
      </>
    )

  return (
    <>
      <div className={style['component-screen']} style={{ top: headerTop - 40 }}></div>
      <div
        className={classNames(style.component, {
          [style['component-static']]: !CSS_SUPPORTS_STICKY
        })}
        style={{ top: headerTop }}
      >
        <div className={`${style.block} p-col-12`}>
          <div className="p-fluid p-field p-col-3">
            <Dropdown
              data-testid="stat-comments-questions"
              className="p-dropdown-rounded p-dropdown-specials"
              placeholder="Select Question"
              value={selectedQuestion || DEFAULT_SELECTED_QUESTION}
              options={questionsListOptions}
              optionValue="value"
              optionLabel="text"
              itemTemplate={itemTemplate}
              valueTemplate={valueTemplate}
              fixedDropdownDir="bottom"
              disabled={readOnly}
              onChange={onSelectQuestion}
            />
          </div>
          <div className="p-field">
            <Button
              className="p-button-rounded p-button-stat"
              label={isDownloading ? `Downloading ${downloadProgress}%` : 'DOWNLOAD'}
              disabled={isDownloadDisabled}
              onClick={isDownloadDisabled ? null : onDownloadAllClick}
            />
          </div>
          <div className="p-fluid p-field p-col-3">
            <Dropdown
              data-testid="stat-comments-order"
              className="p-dropdown-rounded p-dropdown-specials"
              placeholder="Sort"
              value={orderBy}
              options={orderByOptions}
              optionValue="value"
              optionLabel="text"
              itemTemplate={orderByItemTemplate}
              valueTemplate={valueTemplate}
              fixedDropdownDir="bottom"
              disabled={readOnly}
              onChange={onSelectOrderBy}
            />
          </div>
          <div className="p-field">
            {12 >= totalImageResponses ? (
              <span className="per-page">{totalImageResponses} found</span>
            ) : (
              <Dropdown
                data-testid="stat-comments-page-size"
                className="p-dropdown-rounded p-dropdown-specials p-dropdown-left"
                value={pageSize}
                options={PER_PAGE_OPTIONS_IMAGES}
                optionValue="value"
                optionLabel="text"
                itemTemplate={itemTemplate}
                valueTemplate={pageSizeValueTemplate}
                fixedDropdownDir="bottom"
                disabled={readOnly}
                onChange={onLoadPerPage}
              />
            )}
          </div>
          {1 < totalPages ? (
            <div className="p-field p-right">
              <Paginator
                data-testid="stat-comments-paginator"
                first={currentPage - 1}
                rows={1}
                totalRecords={totalPages}
                template={{
                  layout: `PrevPageLink CurrentPageReport NextPageLink`,
                  PrevPageLink: options => (
                    <button
                      type="button"
                      className={options.className}
                      onClick={options.onClick}
                      disabled={options.disabled}
                    >
                      <i className="p-paginator-icon pi icon-aytmfd-prev" />
                    </button>
                  ),
                  NextPageLink: options => (
                    <button
                      type="button"
                      className={options.className}
                      onClick={options.onClick}
                      disabled={options.disabled}
                    >
                      <i className="p-paginator-icon pi icon-aytmfd-next" />
                    </button>
                  )
                }}
                currentPageReportTemplate="{currentPage}/{totalPages}"
                onPageChange={event => onPageChange(event.page + 1)}
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}

SurveyHeaderComments.propTypes = {
  readOnly: PropTypes.bool,
  isHighlightSearchTerms: PropTypes.bool,
  pageData: PropTypes.array,
  questionsList: PropTypes.array,
  currentResponse: PropTypes.object,
  setIsHighlightSearchTerms: PropTypes.func,
  onSelectedQuestionChange: PropTypes.func,
  onPageChange: PropTypes.func,
  onUpdateSchema: PropTypes.func,
  fetchData: PropTypes.func
}

SurveyHeaderComments.displayName = 'SurveyHeader'

export default SurveyHeaderComments
